import { useMutation } from '@tanstack/react-query';
import { Form, Formik } from 'formik';
import { useEffect } from 'react';
import * as yup from 'yup';
import processorSettingService from '../../../../../../../../services/api/payments/processorSettingService';
import Modal from '../../../../../../../../components/modals/Modal';
import { Button } from '../../../../../../../../components/common';
import { FormInput, SelectInput } from '../../../../../../../../components/form';


function ModalProcessorSettingsForm({ client, open, processorSetting, onClose, onAdded, onUpdated }: Props) {

    useEffect(() => {
        if (open) {
            saveMutation.reset();
        }
    }, [open]);


    const saveMutation = useMutation({
        mutationFn: (data: any) => {
            return processorSetting ? processorSettingService.updateSettings(client?.id, processorSetting.id, data) : processorSettingService.createSetting(data);
        }
    });


    const handleFormSubmit = async (formData: any, { resetForm }: any) => {
        saveMutation.mutate({
            ...formData,
            clientId: client?.id,
        }, {
            onSuccess: (data) => {
                onClose();

                if (processorSetting && onUpdated) {
                    onUpdated(data);
                }

                if (!processorSetting && onAdded) {
                    onAdded(data);
                }
            },
            onError: () => {
            }
        });
    }


    return (
        <Modal open={open} onClose={false ? () => null : onClose} closeOnOutsideClicked={false}>
            <div className="inline-block w-[30rem] max-w-full">
                <div className="flex justify-between mb-5 px-10 pt-8">
                    <h2 className="font-medium text-xl">
                        {processorSetting ? "Edit Processor Setting" : "Add Processor Setting"}
                    </h2>

                    <button type="button" onClick={onClose} className="text-2xl text-gray-500 [&:not(:disabled)]:hover:text-gray-600 dark:text-gray-600 dark:[&:not(:disabled)]:hover:text-gray-500 focus:outline-none" disabled={saveMutation.isPending}>
                        <i className="ri-close-line"></i>
                    </button>
                </div>

                <div className='max-h-[80vh] overflow-y-auto px-10 pb-6'>
                    <Formik
                        initialValues={{
                            processorName: processorSetting?.processorName || '',
                            dataSource: processorSetting?.dataSource || '',
                            channel: processorSetting?.channel || '',
                        }}
                        validationSchema={validationSchema}
                        onSubmit={handleFormSubmit}
                    >
                        {({ values, errors, touched, setErrors, setFieldValue, setFieldTouched, submitCount, isValid }) => (
                            <Form className="">
                                {
                                    saveMutation.error &&
                                    <p className='text-red-500 mb-4'>{saveMutation.error.message}</p>
                                }


                                <div className="mb-8">
                                    <label htmlFor="input-name" className='block text-sm mb-2'>Processor Name</label>
                                    <FormInput type="text" name="processorName" id="input-name" className="w-full" placeholder="Enter name" />
                                    {touched['processorName'] && errors['processorName'] && <p className='text-sm text-red-500 mt-2'>Processor settings name is required</p>}
                                </div>


                                <div className="mb-8">
                                    <label htmlFor="modal-select-source" className='block text-sm mb-2'>Data Source</label>
                                    <SelectInput
                                        items={[
                                            { label: "From MineX 360", value: "MINEX" },
                                            { label: "Fidelity", value: "FIDELITY" },
                                            { label: "From Direct Warrantee", value: "DW" },
                                        ]}
                                        placeholder="Select data source"
                                        value={values["dataSource"]}
                                        onChange={(selected: any) => setFieldValue("dataSource", selected.value)}
                                        onBlur={() => setFieldTouched('dataSource', true)}
                                    />
                                    {touched['dataSource'] && errors['dataSource'] && <p className='text-sm text-red-500 mt-2'>Data source is required</p>}
                                </div>


                                <div className="mb-10">
                                    <label htmlFor="modal-select-source" className='block text-sm mb-2'>Payment Channel</label>
                                    <SelectInput
                                        items={[
                                            // [ ACCOUNT, MOMO, CARD, CASH, CREDIT, BANK_SLIP, UNKNOWN, ONLINE, OFFLINE, CANTEEN, IN_BRANCH, ONLINE_MOMO, CHEQUE, BANK_TRANSFER ]
                                            { label: "Account", value: "ACCOUNT" },
                                            { label: "MoMo", value: "MOMO" },
                                            { label: "Online MoMo", value: "ONLINE_MOMO" },
                                            { label: "Card", value: "CARD" },
                                            { label: "Cash", value: "CASH" },
                                            { label: "Credit", value: "CREDIT" },
                                            { label: "Bank Slip", value: "BANK_SLIP" },
                                            { label: "Online", value: "ONLINE" },
                                            { label: "Canteen", value: "CANTEEN" },
                                            { label: "In Branch", value: "IN_BRANCH" },
                                            { label: "Cheque", value: "CHEQUE" },
                                            { label: "Bank Transfer", value: "BANK_TRANSFER" },
                                        ]}
                                        placeholder="Select channel"
                                        value={values["channel"]}
                                        onChange={(selected: any) => setFieldValue("channel", selected.value)}
                                        onBlur={() => setFieldTouched('channel', true)}
                                    />
                                    {touched['channel'] && errors['channel'] && <p className='text-sm text-red-500 mt-2'>Channel is required</p>}
                                </div>

                                <div className='flex justify-end items-center'>
                                    <Button type='button' onClick={onClose} className='!text-gray-500 dark:!text-white bg-gray-100 hocus:bg-gray-200  dark:bg-gray-800 dark:hocus:bg-gray-600 py-2.5 mr-6'>
                                        Cancel
                                    </Button>

                                    <Button type='submit' loading={saveMutation.isPending} className='' disabled={saveMutation.isPending}>
                                        {processorSetting ? "Save" : "Create Setting"}
                                    </Button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </div>
        </Modal>
    );
}


interface Props {
    open: boolean;
    client: any;
    processorSetting: any;
    onClose: () => void;
    onAdded?: (data: any) => void;
    onUpdated?: (data: any) => void;
}


const validationSchema = yup.object().shape({
    processorName: yup.string().trim().required(),
    dataSource: yup.string().trim().required(),
    channel: yup.string().trim().required(),
});


export default ModalProcessorSettingsForm;